import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, Platform } from 'react-native'
import { Button, Text, Spinner, Layout, RangeDatepicker, Radio, Datepicker, useTheme } from '@ui-kitten/components'
import { LineChart } from "react-native-chart-kit"
import { Text as TextSVG } from "react-native-svg"
import { format, addDays } from 'date-fns'

import { getStatsForDays } from '../../global/Firebase/firebase'
import { UserContext } from '../../global/UserProvider'
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose, getLocalStoreDate } from '../../utils/timezoneFunctions'
import { constantsAtom, currencyAtom, currencyFormatterAtom, localeDateServiceAtom, storesDictAtom, usersListAtom, userRoleAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { ModalView } from '../../components/ModalView'
import { validateDailyStats } from '../../utils/validationFunctions'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { exportToCSV, exportToExcel } from '../../utils/exportUtils'

/**
 * ATL-1013 — the report and the "recalculate" tool must accept the SAME days.
 *
 * They did not: the range picker above allowed any day up to 2029, while the recalculate picker
 * inside the "found an error" modal was capped at `new Date()`. A business day can legitimately
 * be ahead of the reading device's clock — a sale is booked onto the business day of ITS OWN
 * till's clock, so a till running fast writes real sales onto a forward-dated day. At ИП Смак
 * that produced a report the merchant could open (02/09/2026, showing two sales that a Firestore
 * batch replay had double-counted) but could not repair, because the repair picker refused to
 * select the day the report had just displayed.
 */
const REPORT_MIN_DATE = new Date('2020-12-01')
const REPORT_MAX_DATE = new Date('2029-12-31')

const getInitialRange = () => {
  let d = new Date()
  var day = d.getDay(),
  diff = d.getDate() - day + (day == 0 ? -6:1) // adjust when day is sunday
  let monday = new Date(d.setDate(diff))
  return {
    startDate: monday,
    endDate: new Date(),
  }
}

export default function SalesStats({ navigation, route }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const currency = useAtomValue(currencyAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const constants = useAtomValue(constantsAtom)
  const usersList = useAtomValue(usersListAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const userRole = useAtomValue(userRoleAtom)
  const db = context.db
  const [data, setData] = useState(null)
  const [store, setStore] = useState(context.user.store?.id)
  const [range, setRange] = useState(getInitialRange())
  const [groupBy, setGroupBy] = useState('paymentMethods')
  const [foundErrorModalIsShown, setFoundErrorModalIsShown] = useState(false)
  const [validationDate, setValidationDate] = useState(new Date())
  const [isValidationLoading, setIsValidationLoading] = useState(false)
  const _rangePicker = React.createRef()
  const _datepicker = React.createRef()

  useEffect(() => {
    getDataFromFB(range, store)
  }, [])

  const getDataFromFB = async (newRange, store) => {
    if (newRange.startDate != null && newRange.endDate != null) {
      _rangePicker.current?.blur()
      setData(null)
      newRange = dateRangeWithTimezoneAndAutoclose(newRange, store === 'all' ? null : [store])
      var saleAmount = 0
      var returnsAmount = 0
      var returnsQuantity = 0
      var salesQuantity = 0

      var paymentMethodsSaleAmount = {}
      var paymentMethodsSaleQuantity = {}
      var paymentMethodsReturnAmount = {}
      var paymentMethodsReturnQuantity = {}
      var paymentMethodsData = []

      var purchaseMethodsSaleAmount = {}
      var purchaseMethodsSaleQuantity = {}
      var purchaseMethodsReturnAmount = {}
      var purchaseMethodsReturnQuantity = {}
      var purchaseMethodsData = []

      var employeesSaleAmount = {}
      var employeesSaleQuantity = {}
      var employeesReturnAmount = {}
      var employeesReturnQuantity = {}
      var employeesData = []
      
      var salesAmountData = [] 
      var returnsAmountData = []
      var returnsQuantityData = []
      var salesQuantityData = []
      var salesAverageAmountData = []
      

      var salesLabels = []

      let dSbyDate = await getStatsForDays(db, newRange, (store === 'all' ? null : store), true)
      
      let date = addDays(newRange.startDate, -1)
      do {
        date = addDays(date, 1)
        let value = dSbyDate[format(getLocalStoreDate(store === 'all' ? context.user.store.id : store, date), 'd/M/y E')] || {}
        saleAmount += (value.saleAmount || 0)
        returnsAmount += (value.returnsAmount || 0)
        returnsQuantity += (value.returnsQuantity || 0)
        salesQuantity += (value.saleQuantity || 0)
        salesAmountData.push(value.saleAmount || 0)
        salesQuantityData.push(value.saleQuantity || 0)
        returnsAmountData.push(value.returnsAmount || 0)
        returnsQuantityData.push(value.returnsQuantity || 0)
        salesAverageAmountData.push((value.saleAmount || 0)/(value.saleQuantity || 1))
        salesLabels.push(format(getLocalStoreDate(store === 'all' ? context.user.store.id : store, date), 'd/M/y E'))
        let paymentMethodsDataForDate = {}
        for (let paymentMethod of constants.paymentMethods.data) {
          paymentMethodsSaleAmount[paymentMethod.id] = (paymentMethodsSaleAmount?.[paymentMethod.id] || 0) + (value?.[paymentMethod.id + 'SalesAmount'] || 0)
          paymentMethodsSaleQuantity[paymentMethod.id] = (paymentMethodsSaleQuantity?.[paymentMethod.id] || 0) + (value?.[paymentMethod.id + 'SalesQuantity'] || 0)
          paymentMethodsReturnAmount[paymentMethod.id] = (paymentMethodsReturnAmount?.[paymentMethod.id] || 0) + (value?.[paymentMethod.id + 'ReturnsAmount'] || 0)
          paymentMethodsReturnQuantity[paymentMethod.id] = (paymentMethodsReturnQuantity?.[paymentMethod.id] || 0) + (value?.[paymentMethod.id + 'ReturnsQuantity'] || 0)
          paymentMethodsDataForDate[paymentMethod.id] = {
            saleAmount: value[paymentMethod.id + 'SalesAmount'] || 0,
            saleQuantity: value[paymentMethod.id + 'SalesQuantity'] || 0,
            returnsAmount: value[paymentMethod.id + 'ReturnsAmount'] || 0,
            returnsQuantity: value[paymentMethod.id + 'ReturnsQuantity'] || 0,
          }
        }
        paymentMethodsData.push(paymentMethodsDataForDate)
        let purchaseMethodsDataForDate = {}
        for (let purchaseMethod of constants.purchaseMethods.data) {
          purchaseMethodsSaleAmount[purchaseMethod.id] = (purchaseMethodsSaleAmount?.[purchaseMethod.id] || 0) + (value?.[purchaseMethod.id + 'SalesAmount'] || 0)
          purchaseMethodsSaleQuantity[purchaseMethod.id] = (purchaseMethodsSaleQuantity?.[purchaseMethod.id] || 0) + (value?.[purchaseMethod.id + 'SalesQuantity'] || 0)
          purchaseMethodsReturnAmount[purchaseMethod.id] = (purchaseMethodsReturnAmount?.[purchaseMethod.id] || 0) + (value?.[purchaseMethod.id + 'ReturnsAmount'] || 0)
          purchaseMethodsReturnQuantity[purchaseMethod.id] = (purchaseMethodsReturnQuantity?.[purchaseMethod.id] || 0) + (value?.[purchaseMethod.id + 'ReturnsQuantity'] || 0)
          purchaseMethodsDataForDate[purchaseMethod.id] = {
            saleAmount: value[purchaseMethod.id + 'SalesAmount'] || 0,
            saleQuantity: value[purchaseMethod.id + 'SalesQuantity'] || 0,
            returnsAmount: value[purchaseMethod.id + 'ReturnsAmount'] || 0,
            returnsQuantity: value[purchaseMethod.id + 'ReturnsQuantity'] || 0,
          }
        }
        purchaseMethodsData.push(purchaseMethodsDataForDate)
        let employeesDataForDate = {}
        for (let employee of usersList) {
          employeesSaleAmount[employee.id] = (employeesSaleAmount?.[employee.id] || 0) + (value?.[employee.id + 'SalesAmount'] || 0)
          employeesSaleQuantity[employee.id] = (employeesSaleQuantity?.[employee.id] || 0) + (value?.[employee.id + 'SalesQuantity'] || 0)
          employeesReturnAmount[employee.id] = (employeesReturnAmount?.[employee.id] || 0) + (value?.[employee.id + 'ReturnsAmount'] || 0)
          employeesReturnQuantity[employee.id] = (employeesReturnQuantity?.[employee.id] || 0) + (value?.[employee.id + 'ReturnsQuantity'] || 0)
          employeesDataForDate[employee.id] = {
            saleAmount: value[employee.id + 'SalesAmount'] || 0,
            saleQuantity: value[employee.id + 'SalesQuantity'] || 0,
            returnsAmount: value[employee.id + 'ReturnsAmount'] || 0,
            returnsQuantity: value[employee.id + 'ReturnsQuantity'] || 0,
          }
        }
        employeesData.push(employeesDataForDate)
      } while (format(date, 'd/M/y E') !== format(newRange.endDate, 'd/M/y E'))

      // e2e capture hook: the final per-day + total sales numbers, keyed the same way
      // the report renders them. Used by sales-report-timezone.spec.ts to assert the
      // business-day bucketing across timezone/autoclose configs (ATL-674).
      console.log('[SALES_STATS_DATA]', JSON.stringify({ store, saleAmount, salesQuantity, salesLabels, salesAmountData, salesQuantityData }))

      setData({
        saleAmount,
        returnsAmount,
        returnsQuantity,
        salesQuantity,

        paymentMethodsSaleAmount,
        paymentMethodsSaleQuantity,
        paymentMethodsReturnAmount,
        paymentMethodsReturnQuantity,
        paymentMethodsData,

        purchaseMethodsSaleAmount,
        purchaseMethodsSaleQuantity,
        purchaseMethodsReturnAmount,
        purchaseMethodsReturnQuantity,
        purchaseMethodsData,

        employeesSaleAmount,
        employeesSaleQuantity,
        employeesReturnAmount,
        employeesReturnQuantity,
        employeesData,
        
        salesAmountData,
        salesQuantityData,
        salesAverageAmountData,
        returnsAmountData,
        returnsQuantityData,
        
        salesLabels,
      })
    }
  }

  const TimeChart = ({timeData, legend, dotManipulation, ySuffix}) => {
    if (Platform.OS === 'android') {
      return null
    }
    return (
      <LineChart 
        data={{
          labels: data.salesLabels,
          datasets: [
            {
              data: timeData
            },
          ],
          legend: [legend]
        }}
        width={data.salesLabels.length * 100} // from react-native
        height={220}
        // yAxisLabel="  . "
        yAxisSuffix={ySuffix}
        yAxisInterval={1} // optional, defaults to 1
        yLabelsOffset={20}
        chartConfig={{
          backgroundColor: theme['color-primary-700'],
          backgroundGradientFrom: theme['color-primary-600'],
          backgroundGradientTo: theme['color-primary-500'],
          decimalPlaces: 0, // optional, defaults to 2dp
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          style: {
            borderRadius: 16
          },
          propsForDots: {
            r: "6",
            strokeWidth: "2",
            stroke: theme['color-primary-500']
          },
        }}
        renderDotContent={
          ({x, y, index, indexData}) => 
            <TextSVG 
              fill={theme['text-control-color']}
              key={x+y+''}
              stroke="none"
              fontSize="10"
              fontWeight="regular"
              x={x}
              y={y-8}
              textAnchor="middle"
            >
                {dotManipulation(indexData)}
            </TextSVG>
        }
        bezier
        style={{
          borderRadius: 16,
        }}
      /> 
    )
  }
  
  const handleExport = async (type) => {
    try {
      // Transform data into exportable format
      let exportData = data.salesLabels.map((date, index) => {
        const dayData = {
          [t('date')]: date,
          [t('stats.sales')]: data.salesAmountData[index],
          [t('stats.saleQuantityFull')]: data.salesQuantityData[index],
          [t('stats.salesAverageAmount')]: data.salesAverageAmountData[index],
          [t('stats.returns')]: data.returnsAmountData[index],
          [t('stats.returnQuantity')]: data.returnsQuantityData[index],
        }

        constants?.paymentMethods.data.forEach(pm => {
          dayData[t('stats.sales') + ' - ' + pm.name] = data.paymentMethodsData[index][pm.id]?.['saleAmount'] || 0
          dayData[t('stats.saleQuantityFull') + ' - ' + pm.name] = data.paymentMethodsData[index][pm.id]?.['saleQuantity'] || 0
          dayData[t('stats.returns') + ' - ' + pm.name] = data.paymentMethodsData[index][pm.id]?.['returnsAmount'] || 0
          dayData[t('stats.returnQuantity') + ' - ' + pm.name] = data.paymentMethodsData[index][pm.id]?.['returnsQuantity'] || 0
        })
      
        constants?.purchaseMethods.data.forEach(pm => {
          dayData[t('stats.sales') + ' - ' + pm.name] = data.purchaseMethodsData[index][pm.id]?.['saleAmount'] || 0
          dayData[t('stats.saleQuantityFull') + ' - ' + pm.name] = data.purchaseMethodsData[index][pm.id]?.['saleQuantity'] || 0
          dayData[t('stats.returns') + ' - ' + pm.name] = data.purchaseMethodsData[index][pm.id]?.['returnsAmount'] || 0
          dayData[t('stats.returnQuantity') + ' - ' + pm.name] = data.purchaseMethodsData[index][pm.id]?.['returnsQuantity'] || 0
        })
    
        usersList.forEach(employee => {
          dayData[t('stats.sales') + ' - ' + employee.name] = data.employeesData[index][employee.id]?.['saleAmount'] || 0
          dayData[t('stats.saleQuantityFull') + ' - ' + employee.name] = data.employeesData[index][employee.id]?.['saleQuantity'] || 0
        })
        
        return dayData
      })

      const filename = 'sales-stats-' + format(range.startDate, 'd-M-y') + '-' + format(range.endDate, 'd-M-y')

      switch (type) {
        case 'csv':
          await exportToCSV(exportData, filename)
          break;
        case 'excel':
          await exportToExcel(exportData, filename)
          break;
      }
    } catch (error) {
      console.error('Export failed:', error)
      // You might want to show an error message to the user here
    }
  }

  return (    
    <ScrollView style={{padding: 20}}>
      <StoreChooserHorizontal store={store} onChange={(newStore) => {
        setStore(newStore)
        getDataFromFB(range, newStore)
      }} />
      <View> 
        <Text category='label' style={styles.label}>
          {t('stats.groupBy')}
        </Text>
        <Radio style={styles.radio} checked={groupBy === 'paymentMethods'} onChange={() => {setGroupBy('paymentMethods')}}>
          {t('stats.byPaymentMethod')}
        </Radio>
        <Radio style={styles.radio} checked={groupBy === 'purchaseMethods'} onChange={() => {setGroupBy('purchaseMethods')}}>
          {t('stats.byPurchaseMethod')}
        </Radio>
        <Radio style={styles.radio} checked={groupBy === 'employees'} onChange={() => {setGroupBy('employees')}}>
          {t('stats.byEmployee')}
        </Radio>
      </View>
      <View style={styles.dateFilter}>
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          range={range}
          ref={_rangePicker}
          min={REPORT_MIN_DATE}
          max={REPORT_MAX_DATE}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              getDataFromFB(newRange, store)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              getDataFromFB({startDate: newRange.startDate, endDate}, store)
            } else {
              setRange(newRange)
            }
          }}
        />
      </View>

      { data ? (
        <>
          <Text category='h2' style={styles.header}>
            {t('stats.salesStats')}
          </Text>
          <Text category='label' style={styles.label}>
            {t('stats.fullPeriod')}
          </Text>
          <Layout style={styles.tableView}>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.sales')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.saleAmount)}</Text>
            </View>
            { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.paymentMethodsSaleAmount?.[pm.id] || 0)}</Text>
              </View>
            ))}
            { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.purchaseMethodsSaleAmount?.[pm.id] || 0)}</Text>
              </View>
            ))}
            { groupBy === 'employees' && usersList.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.employeesSaleAmount?.[pm.id] || 0)}</Text>
              </View>
            ))}
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.saleQuantityFull')}</Text>
              <Text style={styles.rowItemAmount}>{data.salesQuantity?.toFixed(0)}</Text>
            </View>
            { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{data.paymentMethodsSaleQuantity?.[pm.id]?.toFixed(0) || 0}</Text>
              </View>
            ))}
            { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{data.purchaseMethodsSaleQuantity?.[pm.id]?.toFixed(0) || 0}</Text>
              </View>
            ))}
            { groupBy === 'employees' && usersList.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{data.employeesSaleQuantity?.[pm.id]?.toFixed(0) || 0}</Text>
              </View>
            ))}
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.salesAverageAmount')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.saleAmount / data.salesQuantity) || 0)}</Text>
            </View>
            { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.paymentMethodsSaleAmount?.[pm.id] || 0) / (data.paymentMethodsSaleQuantity?.[pm.id] || 0) || 0)}</Text>
              </View>
            ))}
            { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.purchaseMethodsSaleAmount?.[pm.id] || 0) / (data.purchaseMethodsSaleQuantity?.[pm.id] || 0) || 0)}</Text>
              </View>
            ))}
            { groupBy === 'employees' && usersList.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.employeesSaleAmount?.[pm.id] || 0) / (data.employeesSaleQuantity?.[pm.id] || 0) || 0)}</Text>
              </View>
            ))}
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.returns')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.returnsAmount)}</Text>
            </View>
            { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.paymentMethodsReturnAmount[pm.id] || 0)}</Text>
              </View>
            ))}
            { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.purchaseMethodsReturnAmount[pm.id] || 0)}</Text>
              </View>
            ))}
            {/* { groupBy === 'employees' && usersList.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{(data.employeesReturnAmount[pm.id]?.toFixed(0) || 0))}</Text>
              </View>
            ))} */}
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.returnQuantity')}</Text>
              <Text style={styles.rowItemAmount}>{data.returnsQuantity?.toFixed(0)}</Text>
            </View>
            { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{data.paymentMethodsReturnQuantity[pm.id]?.toFixed(0)}</Text>
              </View>
            ))}
            { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{data.purchaseMethodsReturnQuantity[pm.id]?.toFixed(0)}</Text>
              </View>
            ))}
             {/* { groupBy === 'employees' && usersList.map((pm, pmidx) => (
              <View key={pmidx} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                <Text style={styles.rowItemAmount}>{data.employeesReturnQuantity[pm.id]?.toFixed(0)}</Text>
              </View>
            ))} */}
          </Layout>
          { data.salesAmountData.length > 0 && (
            <ScrollView style ={{marginBottom: 20}} horizontal={true}>
              <View>
                <TimeChart timeData={data.salesAmountData.map((value, index) => {return value/1000})} legend={t('stats.saleAmount')} dotManipulation={(indexData) => {return currencyFormatter.format(indexData*1000)}} ySuffix={'k ' + (currency?.shortText || '')} />
                <TimeChart timeData={data.salesQuantityData} legend={t('stats.saleQuantityFull')} dotManipulation={(indexData) => {return indexData?.toFixed()}} ySuffix='' />
                <TimeChart timeData={data.salesAverageAmountData.map((value, index) => {return value/1000})} legend={t('stats.saleQuantityFull')} dotManipulation={(indexData) => {return currencyFormatter.format(indexData*1000)}} ySuffix={'k ' + (currency?.shortText || '')} />
              </View>
            </ScrollView>
          )}
          <Text category='label' style={styles.label}>
            {t('stats.byDays')}
          </Text>
          { data.salesLabels.map((value, index) => (
            <Layout key={value} style={styles.tableView}>
              <Text category='label' style={styles.label}>
                {value}
              </Text>
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('stats.sales')}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.salesAmountData[index])}</Text>
              </View>
              { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.paymentMethodsData[index][pm.id]?.['saleAmount'])}</Text>
                </View>
              ))}
              { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.purchaseMethodsData[index][pm.id]?.['saleAmount'])}</Text>
                </View>
              ))}
              { groupBy === 'employees' && usersList.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.employeesData[index][pm.id]?.['saleAmount'])}</Text>
                </View>
              ))}
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('stats.saleQuantityFull')}</Text>
                <Text style={styles.rowItemAmount}>{data.salesQuantityData[index]?.toFixed(0)}</Text>
              </View>
              { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.paymentMethodsData[index][pm.id]?.['saleQuantity']?.toFixed(0)}</Text>
                </View>
              ))}
              { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.purchaseMethodsData[index][pm.id]?.['saleQuantity']?.toFixed(0)}</Text>
                </View>
              ))}
              { groupBy === 'employees' && usersList.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.employeesData[index][pm.id]?.['saleQuantity']?.toFixed(0)}</Text>
                </View>
              ))}
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('stats.salesAverageAmount')}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.salesAverageAmountData[index])}</Text>
              </View>
              { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.paymentMethodsData[index][pm.id]?.['saleAmount'] / data.paymentMethodsData[index][pm.id]?.['saleQuantity']) || 0)}</Text>
                </View>
              ))}
              { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.purchaseMethodsData[index][pm.id]?.['saleAmount'] / data.purchaseMethodsData[index][pm.id]?.['saleQuantity']) || 0)}</Text>
                </View>
              ))}
              { groupBy === 'employees' && usersList.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format((data.employeesData[index][pm.id]?.['saleAmount'] / data.employeesData[index][pm.id]?.['saleQuantity']) || 0)}</Text>
                </View>
              ))}
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('stats.returns')}</Text>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.returnsAmountData[index])}</Text>
              </View>
              { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.paymentMethodsData[index][pm.id]?.['returnsAmount'])}</Text>
                </View>
              ))}
              { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(data.purchaseMethodsData[index][pm.id]?.['returnsAmount'])}</Text>
                </View>
              ))}
              {/* { groupBy === 'employees' && usersList.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.employeesData[index][pm.id]?.['returnsAmount'])}</Text>
                </View>
              ))} */}
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('stats.returnQuantity')}</Text>
                <Text style={styles.rowItemAmount}>{data.returnsQuantityData[index]?.toFixed(0)}</Text>
              </View>
              { groupBy === 'paymentMethods' && constants?.paymentMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.paymentMethodsData[index][pm.id]?.['returnsQuantity']?.toFixed(0)}</Text>
                </View>
              ))}
              { groupBy === 'purchaseMethods' && constants?.purchaseMethods.data.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.purchaseMethodsData[index][pm.id]?.['returnsQuantity']?.toFixed(0)}</Text>
                </View>
              ))}
              {/* { groupBy === 'employees' && usersList.map((pm, pmidx) => (
                <View key={pmidx} style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{'- ' + pm.name}</Text>
                  <Text style={styles.rowItemAmount}>{data.employeesData[index][pm.id]?.['returnsQuantity']?.toFixed(0)}</Text>
                </View>
              ))} */}
            </Layout>
          ))}
        </>
      ) : (
        <View style={styles.spinner}>
          <Spinner size="large" />
        </View>
      )}

      { data && (userRole?.permissions?.['exportImport'] || userRole?.id === 'admin') && (
        <>
          <Button appearance="ghost" status='basic' onPress={() => handleExport('csv')}>{t('csvExport')}</Button>
          <Button appearance="ghost" status='basic' onPress={() => handleExport('excel')}>{t('excelExport')}</Button>
        </>
      )}
      
      <ModalView isVisible={foundErrorModalIsShown} setIsVisible={setFoundErrorModalIsShown}>
        <Text style={{marginBottom: 5}} category="h6">{t('stats.foundError')}</Text>
        <Text>{t('stats.foundErrorDescription')}</Text>
        { storesDict && Object.values(storesDict).length > 1 && (
          <>
            <Text category='label' style={styles.label}>{t('store')}</Text>
            <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
              setStore(newStore)
            }} />
          </>
        )}
        <Text category='label' style={styles.label}>{t('date')}</Text>
        <Datepicker
          placement='top'
          min={REPORT_MIN_DATE}
          max={REPORT_MAX_DATE}
          date={validationDate}
          dateService={localeDateService}
          ref={_datepicker}
          onSelect={(date) => {
            setValidationDate(date)
          }}
          style={{marginBottom: 10}}
        />
        
        { isValidationLoading 
            ? 
              <View style={{alignItems: 'center'}}><Spinner /></View>
            :
              <Button onPress={async () => {
                _datepicker.current.blur()
                setIsValidationLoading(true)
                const screenName = route?.name || navigation?.getState?.()?.routes?.[navigation?.getState?.()?.index]?.name || 'SalesStats'
                await validateDailyStats(validationDate, store, true, {sales: true, spendings: true, workingHours: true}, null, true, false, screenName)
                setIsValidationLoading(false)
              }}>{t('stats.recalculate')}</Button>
        }
      </ModalView>
      <Button appearance="ghost" status='basic' onPress={() => setFoundErrorModalIsShown(true)}>{t('stats.foundError')}</Button>
      <View style={{height: 50}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  header: {
    marginVertical: 10,
  },
  tableView: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
  }, 
  rowItemName: {
    flex: 9
  }, 
  rowItemAmount: {
    flex: 4,
    textAlign: 'right',
  },
  label: {
    marginTop: 5,
    marginBottom: 5,
  },
  spinner: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  radio: {
    marginBottom: 5
  }
})